/**
 * Thin typed wrapper over the Telegram Mini App runtime (window.Telegram.WebApp,
 * loaded via telegram-web-app.js in index.html). Falls back gracefully when the
 * app runs outside Telegram during local development.
 */

interface TgBackButton {
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface TgHaptics {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  selectionChanged(): void;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe?: { start_param?: string; [k: string]: unknown };
  version?: string;
  platform?: string;
  colorScheme: 'light' | 'dark';
  ready(): void;
  expand(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  /** Optional on older clients — the theme engine guards the calls. */
  onEvent?(event: 'themeChanged', cb: () => void): void;
  offEvent?(event: 'themeChanged', cb: () => void): void;
  BackButton: TgBackButton;
  HapticFeedback: TgHaptics;
  /** Открывает Telegram-ссылку в самом клиенте; принимает ТОЛЬКО хост t.me. */
  openTelegramLink?(url: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export const tg: TgWebApp | undefined =
  typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;

export const isTelegram = Boolean(tg && tg.initData);

/** Telegram client platform ('android'|'ios'|'tdesktop'|'web'…) and app version. */
export const tgPlatform: string = tg?.platform ?? 'web';
export const tgVersion: string = tg?.version ?? '';

/** Deep-link payload (?startapp=…) — for attribution. */
export function getStartParam(): string | null {
  return tg?.initDataUnsafe?.start_param ?? null;
}

/** Raw initData string to send to the API ('dev' triggers server bypass locally). */
export function getInitData(): string {
  return tg?.initData || 'dev';
}

export function initTelegram(): void {
  if (!tg) return;
  tg.ready();
  tg.expand();
  // Header/background colors are theme-dependent — set by theme.ts applyTheme().
}

/** Telegram-ссылка внутри Telegram; вне Telegram — обычная новая вкладка.
 *  Важно: telegram-web-app.js грузится и в обычном браузере, поэтому метод
 *  openTelegramLink существует всегда — но вне Telegram его вызов уходит в
 *  пустоту. Выбираем путь по isTelegram (живой initData), а не по наличию метода.
 *  Хосты: клиентский openTelegramLink принимает ТОЛЬКО t.me (ссылку обрабатывает
 *  сам Telegram, в сеть она не ходит — блокировка t.me ей не мешает); в браузере
 *  наоборот открываем через зеркало telegram.me, т.к. t.me блокируют провайдеры. */
export function openTelegramLink(url: string): void {
  if (isTelegram && tg?.openTelegramLink) {
    tg.openTelegramLink(url.replace('//telegram.me/', '//t.me/'));
  } else {
    window.open(url.replace('//t.me/', '//telegram.me/'), '_blank', 'noopener');
  }
}

export const haptics = {
  tap: () => tg?.HapticFeedback.selectionChanged(),
  success: () => tg?.HapticFeedback.notificationOccurred('success'),
  error: () => tg?.HapticFeedback.notificationOccurred('error'),
  impact: () => tg?.HapticFeedback.impactOccurred('medium'),
};

export const backButton = {
  show(cb: () => void) {
    if (!tg) return;
    tg.BackButton.onClick(cb);
    tg.BackButton.show();
  },
  hide(cb: () => void) {
    if (!tg) return;
    tg.BackButton.offClick(cb);
    tg.BackButton.hide();
  },
};
